/**
 @module mediaManager
 */

//TODO: Add a param validation on the below functions ot enhance the params management and error reporting
import { Validator } from "jsonschema";
import { services, sections } from "../../../constants";
import { t } from "web-utils-strings";
import { layoutTokenStore, token } from "../../store";
import errors from "../../errors/index";
import {
  mediaListSchema,
  mediaItemSchema,
  heartBeatSchema,
  mediaItemExtraIdSchema,
  feedsQueryParamsSchema,
  feedSectionNameSchema,
  layoutSchema
} from "../../../schema/index";
import isBrowser from "../../../utils/isBrowser";
import {
  fromTitlesCache,
  toTitlesCache,
  toTitleMemoryCache,
  fromTitleMemoryCache,
  fromSeasonsCache,
  toSeasonsCache,
  toSeasonsMemoryCache,
  fromSeasonsMemoryCache,
  fromEpisodesCache,
  toEpisodesCache,
  toEpisodesMemoryCache,
  fromEpisodesMemoryCache,
  fromEpisodeCache,
  toGenresCache,
  fromGenresCache,
  toCatalogueItemCache,
  fromCatalogueItemCache,
  toCatalogueItemMemoryCache,
  fromCatalogueItemMemoryCache,
  toCatalogueItemByGenreIdCache,
  fromCatalogueItemByGenreIdCache,
  toCatalogueItemByGenreIdMemoryCache,
  fromCatalogueItemByGenreIdMemoryCache
} from "./mediaCacheDecorators";
import {
  toLayoutCache,
  fromLayoutCache,
  toLayoutMemoryCache,
  fromLayoutMemoryCache,
  clearLayoutOnWatchlistUpdate,
  toLayoutTokenStore,
  shuffleLayoutStaticModules,
  toChannelEventsCache,
  toChannelEventsMemoryCache,
  fromChannelEventsMemoryCache,
  fromChannelEventsCache
} from "./layoutCacheDecorators";
import addTimeMarkersToCachedTitle from "./addTimeMarkersToCachedTitle";
import { mediaErrorCodes } from "./mediaErrorCodes";

const validator = new Validator();

/**
 * Validate config object which is received in order to send it with the request to the layout middleware
 *
 * @param {object} config contains the limits and positions for fetching the carousels and hero main data
 * @private
 */
function validateLayoutConfig(config = {}) {
  validator.addSchema(layoutSchema, "layoutConfigSchema");

  const validation = validator.validate(config, layoutSchema);
  if (validation.errors.length) {
    throw new Error(errors.layout.INVALID_LAYOUT_CONFIG);
  }
  return true;
}

/**
 * Returns the credits object with the properties values as milliseconds.
 * First of all it checks if these values are not already in milliseconds
 * @param {object} credits
 * @return {object}
 */
function parseCreditsPropertiesToMilliseconds(credits) {
  const { introEndSeconds, outroStartSeconds } = credits;
  if (
    (introEndSeconds !== undefined && parseInt(introEndSeconds / 1000) === 0) ||
    (outroStartSeconds !== undefined && parseInt(outroStartSeconds / 1000) > 0)
  ) {
    Object.keys(credits).forEach(key => (credits[key] = credits[key] * 1000));
  }
  return credits;
}

/**
 * Retrieve and format the filmstrip data file.
 *
 * @param {string} filmstripLocation URL of the filmstrip data file
 */
async function getFilmstripData(filmstripLocation) {
  // FIXME This code is repeated at least in tracks.js. It should be moved to a decorator or single function instead
  const headers = Object.assign({}, this.http.defaults.headers);
  delete this.http.defaults.headers["Client-Type"];
  try {
    const response = await this.http.get(this.parseNonSecureMediaContentUrl(filmstripLocation));
    return extractRelevantThumbnails(response.data);
  } catch (error) {
    console.log(`getFilmstripData method failed: ${error.message}`);
  } finally {
    Object.assign(this.http.defaults.headers, headers);
  }
}

/**
 * Formats the filmstrip raw data.
 *
 * @param filmstripData
 * @return {{thumbnails: *}}
 * @private
 */
function extractRelevantThumbnails(filmstripData) {
  const secondsPerThumbnail = filmstripData.endTime / filmstripData.imageCount;

  const thumbnails = filmstripData.thumbnails.map((thumbnail, index) => ({
    thumbnail,
    timestamp: Math.floor(secondsPerThumbnail * index)
  }));

  return {
    thumbnails
  };
}

/**
 * Validate the media list specific params to be sent with the request trailing query
 *
 * @param {object} mediaListParams query params which should be sent with the request
 * @param mediaListParams
 * @param schemaName
 * @param paramSchema
 * @return {boolean}
 * @private
 */
function validateMediaListsParams(mediaListParams, schemaName, paramSchema) {
  validator.addSchema(mediaListSchema, schemaName);
  const validation = validator.validate(mediaListParams, paramSchema);
  if (validation.errors.length) {
    throw new Error(errors.mediaList.WRONG_MEDIALIST_PARAMS);
  }
  return true;
}

/**
 * Validate the media list item and the listId presents specific params to be sent with the request trailing query
 *
 * @param {string} listId which is the userMediaListId to add titles to it
 * @param {object} mediaListParams query params which should be sent with the request
 * @private
 */
function validateMediaItem(listId, mediaItemParams) {
  validator.addSchema(mediaItemSchema, "MediaItem");
  const validation = validator.validate(mediaItemParams, mediaItemSchema);
  if (validation.errors.length || !listId) {
    throw new Error(errors.mediaList.WRONG_MEDIALIST_PARAMS);
  }
}

/**
 * Validate catalogue params to be valid and also check the sectionName as a mandatory field
 *
 * @param {string} sectionName Section name to get the catalogue related titles
 * @param {object} params catalogue param related to pagination & sorting and user information
 * @private
 */
function validateCatalogueParams(sectionName, params) {
  const paramsValidation = validator.validate(params, feedsQueryParamsSchema);
  const sectionNameValidation = validator.validate({ ...{ sectionName } }, feedSectionNameSchema);
  if (paramsValidation.errors.length || sectionNameValidation.errors.length) {
    throw new Error(errors.feeds.MISSING_CATALOGUE_PARAMS);
  }
}

/**
 * Validate catalogue params to be valid and also check the genreTagId as a mandatory field
 *
 * @param {string} genreTagId Genre Tag id to get the catalogue related titles
 * @param {object} params catalogue param related to pagination & sorting and user information
 * @private
 */
function validateCatalogueGenreParams(genreTagId, params) {
  const paramsValidation = validator.validate(params, feedsQueryParamsSchema);
  if (paramsValidation.errors.length || !Number.isInteger(+genreTagId)) {
    throw new Error(errors.feeds.MISSING_CATALOGUE_PARAMS);
  }
}

/**
 * Validate if the token is present and if not to throw a login error
 * @return {boolean}
 * @private
 */
async function validateToken() {
  if (!!(await token.getToken())) {
    return true;
  }
  throw new Error(errors.mediaList.USER_NOT_LOGGED_IN);
}

/**
 * This function transforms the response received from the media layout v2 MW endpoint by merging the layouts and
 * modules arrays into a single array with all the information. The original object received from the MW contains two
 * main properties: layouts and modules. Layouts contains the total amount of modules existing on the requested section
 * and the lightweight list of modules. The modules object contains a list of objects containing all the information
 * related to the modules requested on the call to the MW (check mediaManager.getLayout() for more information).
 * What this function does is to extend the objects contained in the layouts array by adding to the corresponding items
 * the deep data contained in the modules array. In case there is no layout object in the response, because sometimes
 * the response only contains the modules part, the generated object will be composed only by the contents of the
 * modules array.
 * @param {Object} layout An object containing the original response received from the layouts v2 endpoint
 * @return {Object} A new object containing the following properties:
 * - total: Specifies the total amount of modules present in the section of the layout that was requested to the MW
 * - modules: A list of all the modules present in the layout whose modules have been extended with the full info of
 * those that were specifically requested in the call to getLayouts.
 */
function transformLayoutResponse(layout = {}) {
  // TODO Probably this logic is better suited at MW side
  const transformedLayout = {};
  if (layout.layout) {
    transformedLayout.total = layout.layout.total;
    layout.modules.forEach(module => {
      const moduleToExtend = layout.layout.modules.find(layoutModule => layoutModule.id === module.id);
      moduleToExtend.titles = module.titles;
    });
  }
  transformedLayout.modules = layout.layout ? layout.layout.modules : layout.modules;
  return transformedLayout;
}

const carouselTypes = {
  hero: "Hero",
  editorial: "List5Items",
  becauseYouWatchedMovies: "List5BYWMovies",
  becauseYouWatchedSeries: "List5BYWSeries",
  becauseYouWatchedArabic: "List5BYWArabic",
  becauseYouWatchedKids: "List5BYWKids",
  myList: "List5ML",
  continueWatching: "List5CW"
};

/**
 *
/**
 * Returns the layout requested as a Promise.
 *
 * @param section
 * @param {object} config contains the limits and positions for fetching the modules and hero main data
 * @param {number} config.initialmodule index of the first item in the layout
 * @param {number} config.finalCarousel index of the last item in the layout
 * @param {array} config.modulesByPosition array of numbers return the modules by their index
 * @param {array} config.modulesById array of ids return the modules by their ids
 * @param {boolean} config.avoidCache flag to decide which way response will return either from cache or from the
 * middleware service itself
 * @param {array} config.avoidCacheFor request the modules specified in this property and use cache if possible for the
 * rest. This can accept module [positions/ids]
 * @param {boolean} config.refreshLayout Ensure a fresh copy is requested to the backend. The backend uses the x-token
 * and x-query to match the client calls to a certain version of the layout. If the layout changes the x-token and
 * x-query parameters will not be valid anymore. If refreshLayout comes as true the locally stored x-token and x-query
 * parameters will be cleared and new ones will be requested with a GET layout call. Defaults to false.
 *
 * @return {Promise}
 * @private
 */
async function getLayout(section, config = {}) {
  // TODO Handle the scenario when the tokens have changed --> remove/override cache?
  try {
    validateLayoutConfig(config);
    const supports4k = await this.device.supports4K();
    config.refreshLayout && layoutTokenStore.clear();
    const { xToken, xQuery } = layoutTokenStore.get(section);
    let modules = [];
    if (config.initialModule !== undefined && config.finalModule !== undefined) {
      modules = `${config.initialModule}-${config.finalModule}`;
    } else {
      modules = config.modulesByPosition || config.modulesById || [];
    }
    let endpoint = services.layout[section] || `/v2/layout/${section}`;
    const version = config && config.version && {version: config.version};
    const layout = await this.http.get(
      await this.buildMediaUrl("media", endpoint, null, supports4k, {
        modules,
        origin: this.clientType,
        ...version
      }),
      await this.buildLayoutHeaders(xToken, xQuery)
    );
    // TODO Move this away from here to somewhere better
    await toLayoutTokenStore.call(this, section, layout.data);
    return transformLayoutResponse(layout.data);
  } catch (error) {
    this.handleErrorResponse(error);
  }
}
/**
 * return  xToken, xQuery for the layouts from memory 
 * @param {*} section 
 * @returns 
 */
async function getLayoutToken (section) {
  return layoutTokenStore.get(section) || {};
}
/**
 * Returns the channel events requested as a Promise.
 *
 * @param data
 * @return {Promise}
 */
async function getChannelEvents(data) {
  try {
    let params = this.parseQueryParamsString(data);
    const channelEvents = await this.http.get(await this.buildUrl("media", `${services.channel.events}?${params}`));
    return channelEvents.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns the channel config requested as a Promise.
 *
 * @return {Promise}
 */
async function getChannelConfig() {
  try {
    const channelConfig = await this.http.get(await this.buildUrl("media", `${services.channel.config}`));
    return channelConfig.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns the titles requested as a Promise.
 *
 * @param id
 * @return {Promise}
 */
async function getMovies(id, config = {}) {
  let params =
    (!!config.ensureLocalization &&
      this.lang !== config.ensureLocalization && { lang: `${this.lang},${config.ensureLocalization}` }) ||
    {};
  const movies = await this.http.get(
    await this.buildMediaUrl("media", services.titles.movies, id, null, params),
    await this.getHeaders()
  );
  return movies.data;
}

/**
 * Returns the series info as a Promise.
 *
 * @param seriesId get titleId for the specific series
 * @return {Promise}
 */
async function getSeries(seriesId, config = {}) {
  let params =
    (!!config.ensureLocalization &&
      this.lang !== config.ensureLocalization && { lang: `${this.lang},${config.ensureLocalization}` }) ||
    {};
  const series = await this.http.get(
    await this.buildMediaUrl("media", t(services.titles.series, seriesId), null, null, params),
    await this.getHeaders()
  );
  return series.data;
}

/**
 * Returns the series seasons list info as a Promise.
 *
 * @param seriesId titleId for the specific series.
 * @return {Promise}
 */
async function getSeriesSeasonsList(seriesId, offline = false) {
  const seasonsList = await this.http.get(
    await this.buildMediaUrl("media", t(services.titles.seasons, seriesId), null),
    {
      ...(await this.getHeaders()),
      ...(!!offline && { timeout: this.requestTimeout })
    }
  );
  return seasonsList.data;
}

/**
 * Returns the series season episodes payload as a Promise.
 *
 * @param seriesId titleId for the specific series.
 * @param seasonNo season number to get the data related.
 * @return {Promise}
 */
async function getSeriesSeasonEpisodes(seriesId, seasonNo) {
  const episodes = await this.http.get(
    await this.buildMediaUrl("media", t(services.titles.seasonepisodes, seriesId, seasonNo), null),
    await this.getHeaders()
  );
  return episodes.data;
}

/**
 * Returns the series season exact episode as a Promise.
 *
 * @param seriesId  titleId for the specific series.
 * @param seasonNo  season number to get the data related.
 * @param episodeNo episode number to get the data related.
 * @return {Promise}
 */
async function getSeriesSeasonEpisode(seriesId, seasonNo, episodeNo) {
  const episode = await this.http.get(
    await this.buildMediaUrl("media", t(services.titles.seasonepisode, seriesId, seasonNo, episodeNo), null),
    await this.getHeaders()
  );
  return episode.data;
}

/**
 * Returns the series season info as a Promise.
 *
 * @param seriesId  titleId for the specific series.
 * @param seasonNo  Season number to get the data related.
 * @return {Promise}
 */
async function getSeriesSeason(seriesId, seasonNo) {
  // TODO Add caching to this
  const season = await this.http.get(
    await this.buildMediaUrl("media", t(services.titles.season, seriesId, seasonNo), null),
    await this.getHeaders()
  );
  return season.data;
}

/**
 * Returns the title response which can be also fetched for movie title as a Promise.
 *
 * @param titleId  titleId
 * @param is4K  Indicates if the title is an 4K program
 * @param offline  Indicates if the app is offline
 * @param ensureLocalization  Indicates if there will be title data in other languages
 * @param onlyMediaWithTrailer  if true, only get the episode that has the media with the trailer
 * @return {Promise}
 */
async function getTitle(
  titleId,
  { is4K = false, offline = false, ensureLocalization = false, onlyMediaWithTrailer = false } = {}
) {
  const byMediaAvailabilityTags = encodeURIComponent("unpackagedTrailer|packagedTrailer|noTrailer");
  let params =
    (!!ensureLocalization && this.lang !== ensureLocalization && { lang: `${this.lang},${ensureLocalization}` }) || {};
  onlyMediaWithTrailer && Object.assign(params, { byMediaAvailabilityTags });
  try {
    const title = await this.http.get(
      await this.buildMediaUrl(
        "media",
        t(services.titles.title, titleId),
        null,
        (await this.device.supports4K()) && is4K,
        params
      ),
      {
        ...(await this.getHeaders()),
        ...(!!offline && { timeout: this.requestTimeout })
      }
    );
    return title.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns an object wich contains only the title text.
 *
 * @param titleId  titleId
 * @return {Promise}
 */
async function getTitleText(titleId) {
  try {
    const title = await this.http.get(
      await this.buildMediaUrl("media", t(services.titles.title, titleId), null, false, { fields: "title" }),
      await this.getHeaders()
    );
    return title.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Search for titles on the media Api with array of titles which are related to the search query
 *
 * @param {string} searchTerm search string to pass it through a GET request
 * @param {object} config object that holds extra params to the search endpoint request
 * @return {Promise}
 */
async function search(searchTerm, config = {}) {
  // TODO: Revisit the search mechanism to see how we can cache the search without affecting the data accuracy =)
  const q = searchTerm.toString().trim();
  const searchParams = this.searchConfig;
  if (q.length > 0) {
    Object.assign(searchParams, {
      q: searchTerm,
      ...config
    });
    const searchResult = await this.http.get(
      await this.buildUrlWithTrailingQuery("media", services.search, null, searchParams),
      await this.getHeaders()
    );
    return searchResult.data;
  }
  return new Promise((resolve, reject) => reject(new Error(errors.media.INVALID_SEARCH_TERM)));
}

/**
 * Returns all the user media lists as a Promise
 *
 * @param {object} params Query params which will be added to the trailing query of the request
 * @return {Promise}
 */
async function getUserMediaLists(params, offline = false) {
  // TODO: have a better validation along all the mediaList validation not to make a validation function per endpoint
  validateMediaListsParams(params, "MediaList", mediaListSchema);
  const headers = (await validateToken()) && (await this.getHeaders());
  Object.assign(params, { supports4K: await this.device.supports4K() });
  const mediaList = await this.http.get(
    await this.buildUrlWithTrailingQueryAndAssetTypes("media", services.mediaList.list, null, params),
    {
      ...headers,
      ...(!!offline && { timeout: this.requestTimeout })
    }
  );
  return mediaList.data;
}

/**
 * Returns specific user list is with its saved titles information as a Promise
 *
 * @param {string} listId User specific media list id number to get the titles inside that list
 * @param {*} params Query params which will be added to the trailing query of the request
 * @return {Promise}
 */
async function getUserMediaListById(listId, params) {
  // TODO: have a better validation along all the mediaList validation not to make a validation function per endpoint
  validateMediaListsParams(params, "MediaList", mediaListSchema);
  const headers = (await validateToken()) && (await this.getHeaders());

  const mediaList = await this.http.get(
    await this.buildUrlWithTrailingQuery("media", t(services.mediaList.listId, listId), null, params),
    headers
  );
  return mediaList.data;
}

/**
 * Do a post request to add a new item to a specific user media list
 *
 * @param {string} listId User specific media list id number to get the titles inside that list
 * @param {*} params Body params which will be sent with the post request
 * @return {Promise}
 */
async function addTitleToMediaList(listId, params) {
  // TODO: have a better validation along all the mediaList validation not to make a validation function per endpoint
  validateMediaItem(listId, params);
  const headers = (await validateToken()) && (await this.getHeaders());

  return await this.http.post(
    await this.buildMediaUrl("media", t(services.mediaList.mediaTitle, listId), null),
    params,
    headers
  );
}

/**
 * Do a delete request to remove media item form specific user media list
 *
 * @param {string} listId User media list id to specify which media list we need to remove the title id from
 * @param {string} titleId Title id to the movie/episode which needs to be removed from the list
 * @return {Promise}
 */
async function removeTitleFromMediaList(listId, titleId) {
  // TODO: have a better validation along all the mediaList validation not to make a validation function per endpoint
  if (!listId && !titleId) {
    return new Error(errors.mediaList.WRONG_MEDIALIST_PARAMS);
  }

  const headers = (await validateToken()) && (await this.getHeaders());

  return await this.http.delete(
    await this.buildMediaUrl("media", t(services.mediaList.removeMediaItem, listId, titleId), null),
    headers
  );
}

/**
 * Clear the specified medialist from all the titles
 *
 * @param {string} listId user media list id which needs to be cleared
 * @return {Promise}
 */
async function clearMediaList(listId) {
  // TODO: have a better validation along all the mediaList validation not to make a validation function per endpoint
  if (!listId) {
    return new Error(errors.mediaList.WRONG_MEDIALIST_PARAMS);
  }

  const headers = (await validateToken()) && (await this.getHeaders());

  return await this.http.delete(
    await this.buildMediaUrl("media", t(services.mediaList.mediaTitle, listId), null),
    headers
  );
}

/**
 * Post a heartbeat request after validating the params and token
 *
 * @param {object} params heartbeat params to send with the post request
 * @return {Promise}
 */
async function heartBeat(params) {
  // TODO: have a better validation along all the mediaList validation not to make a validation function per endpoint
  validateMediaListsParams(params, "Heartbeat", heartBeatSchema);

  const headers = (await validateToken()) && (await this.getHeaders());

  return await this.http.post(this.buildUrl("media", services.mediaList.heartBeat), params, headers);
}

/**
 * Post a title body request after validating the params and token
 *
 * @param {*} params title body object which should be sent with the post request as a data
 * @return {Promise}
 */
async function addTitleBodytoMediaList(params) {
  validateMediaListsParams(params, "MediaItemExtraId", mediaItemExtraIdSchema);
  const headers = (await validateToken()) && (await this.getHeaders());

  return await this.http.post(
    await this.buildMediaUrl("media", services.mediaList.addMediaListTitle, null),
    params,
    headers
  );
}

/**
 * Perform a get request to the feeds endpoint and return catalogue related titles depending on
 * the section name provided.
 *
 * @param {string} sectionName The needed catalogue related titles
 * @param {object} params Contains pagination and sorting related to the catalogue endpoint
 */
async function getCatalogueItems(sectionName, params, offline = false) {
  validateCatalogueParams(sectionName, params);
  try {
    const catalogueItems = await this.http.get(
      await this.buildUrlWithTrailingQuery("media", t(services.feeds, sectionName), null, params),
      {
        ...(await this.getHeaders()),
        ...(!!offline && { timeout: this.requestTimeout })
      }
    );
    return catalogueItems.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Perform a get request to the feeds endpoint and return catalogue related titles depending on
 * the genre tag id provided.
 *
 * @param {number} genreTagId genre tag id to get the related catalogue feeds related
 * @param {object} params Contains pagination and sorting related to the catalogue endpoint
 */
async function getCatalogueItemsByGenreId(genreTagId, params) {
  validateCatalogueGenreParams(genreTagId, params);

  const catalogueItems = await this.http.get(
    await this.buildUrlWithTrailingQuery("media", t(services.genreId, genreTagId), null, params),
    await this.getHeaders()
  );
  return catalogueItems.data;
}

/**
 * Function to get genres from middlewares endpoint
 *
 * @param {object} params object representing the parameter which need to be sent with the request
 * @return {Promise}
 */
async function getGenres(params, offline = false) {
  this.lang !== "en" && Object.assign(params, { lang: `${this.lang},en` });
  const config = offline ? { timeout: this.requestTimeout } : {};
  const genres = await this.http.get(
    await this.buildUrlWithTrailingQuery("media", services.genres, null, params),
    config
  );
  return genres.data;
}

/**
 * Returns information related with the time makers (skip intro and outro) of the title which is stored in the cache if it's exists
 * If not, it calls to the method that will do a request to get the data
 *
 * @param {string} mediaGuid Guid of the media title
 * @param {string} titleId Id of the title
 * @return {object}
 */
async function getTitleTimeMarkersFromCachedTitle(mediaGuid, titleId) {
  const { mediaContent } = await getTitle.call(this, titleId);
  const content = mediaContent.find(media => media.mediaGuid === mediaGuid);
  if (!content.credits) {
    return await getTitleTimeMarkersFromRequest.call(this, mediaGuid, titleId);
  }
  return content.credits;
}

/**
 * Returns information related with the time makers (skip intro and outro) of the title doing a request to get the data.
 * In case the marker data is not available in the cache, if the titleId is received, after making the network request to the MW,
 * this method will update the cached title by adding the markers information to the appropriate media object.
 *
 * @param {string} mediaGuid Guid of the media title
 * @param {string} titleId Id of the title
 * @return {object}
 */
async function getTitleTimeMarkersFromRequest(mediaGuid, titleId = null) {
  const url = `${this.backendHosts.media}${services.titles.medias}`;
  const timeMarkers = await this.http.get(url, {
    params: {
      mediaGuid
    },
    ...(await this.getHeaders())
  });
  const { data } = timeMarkers;
  titleId !== null && addTimeMarkersToCachedTitle.call(this, titleId, mediaGuid, data);
  return data;
}

/**
 * Returns information related with the time makers (skip intro and outro) of the title
 *
 * @param {string} mediaGuid Guid of the media title
 * @param {string} titleId If it’s provided, it will be used to look for the title in the local cache and look for the medias inside of it,
 * in order to return the values from the cache
 * @return {Promise}
 */
async function getTitleTimeMarkers({ mediaGuid, titleId = null }) {
  let timeMarkers;
  if (titleId) {
    timeMarkers = await getTitleTimeMarkersFromCachedTitle.call(this, mediaGuid, titleId);
  } else {
    timeMarkers = await getTitleTimeMarkersFromRequest.call(this, mediaGuid);
  }
  return parseCreditsPropertiesToMilliseconds(timeMarkers);
}

/**
 * Media Manager mixing methods for the SDK.
 */
export default {
  getFilmstripData,
  getLayout: isBrowser
    ? toLayoutCache(toLayoutMemoryCache(fromLayoutMemoryCache(fromLayoutCache(shuffleLayoutStaticModules(getLayout)))))
    : getLayout,
  getChannelEvents: isBrowser
    ? toChannelEventsCache(
        toChannelEventsMemoryCache(fromChannelEventsMemoryCache(fromChannelEventsCache(getChannelEvents)))
      )
    : getChannelEvents,
  getMovies: isBrowser
    ? toTitlesCache(toTitleMemoryCache(fromTitleMemoryCache(fromTitlesCache(getMovies))))
    : getMovies,
  getSeries: isBrowser
    ? toTitlesCache(toTitleMemoryCache(fromTitleMemoryCache(fromTitlesCache(getSeries))))
    : getSeries,
  getSeriesSeasonsList: isBrowser
    ? toSeasonsCache(toSeasonsMemoryCache(fromSeasonsMemoryCache(fromSeasonsCache(getSeriesSeasonsList))))
    : getSeriesSeasonsList,
  getSeriesSeason,
  getSeriesSeasonEpisodes: isBrowser
    ? toEpisodesCache(toEpisodesMemoryCache(fromEpisodesMemoryCache(fromEpisodesCache(getSeriesSeasonEpisodes))))
    : getSeriesSeasonEpisodes,
  getSeriesSeasonEpisode: isBrowser ? fromEpisodeCache(getSeriesSeasonEpisode) : getSeriesSeasonEpisode,
  getTitle: isBrowser ? toTitlesCache(toTitleMemoryCache(fromTitleMemoryCache(fromTitlesCache(getTitle)))) : getTitle,
  getTitleText,
  search,
  getUserMediaLists,
  getUserMediaListById,
  addTitleToMediaList: clearLayoutOnWatchlistUpdate(sections.ALL, addTitleToMediaList),
  addTitleBodytoMediaList: clearLayoutOnWatchlistUpdate(sections.ALL, addTitleBodytoMediaList),
  removeTitleFromMediaList: clearLayoutOnWatchlistUpdate(sections.ALL, removeTitleFromMediaList),
  clearMediaList: clearLayoutOnWatchlistUpdate(sections.ALL, clearMediaList),
  heartBeat: clearLayoutOnWatchlistUpdate(sections.ALL, heartBeat),
  mediaErrorCodes,
  getCatalogueItems: isBrowser
    ? toCatalogueItemCache(
        toCatalogueItemMemoryCache(fromCatalogueItemMemoryCache(fromCatalogueItemCache(getCatalogueItems)))
      )
    : getCatalogueItems,
  getCatalogueItemsByGenreId: isBrowser
    ? toCatalogueItemByGenreIdCache(
        toCatalogueItemByGenreIdMemoryCache(
          fromCatalogueItemByGenreIdMemoryCache(fromCatalogueItemByGenreIdCache(getCatalogueItemsByGenreId))
        )
      )
    : getCatalogueItemsByGenreId,
  getGenres: isBrowser ? toGenresCache(fromGenresCache(getGenres)) : getGenres,
  carouselTypes,
  getTitleTimeMarkers,
  getChannelConfig
};
